import { useState, useRef } from 'react';
import { Upload, File, X } from 'lucide-react';
import { toast } from 'sonner';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useImportTool, useSecrets } from '@/hooks';
import type { SystemToolExport } from '@actions/services/tools';
import type { ImportToolModalProps, ParsedToolFile } from './types';
import {
    DropZone,
    UploadIcon,
    DropZoneText,
    DropZoneSubtext,
    SelectedFile,
    FileInfo,
    FileName,
    FileSize,
    RemoveButton,
    ButtonContainer,
    ErrorMessage,
    ModalHeader,
    ModalTitle,
    ModalDescription,
    LoadingText,
    ProgressContainer,
    SecretMappingContainer,
    SecretMappingItem,
    SecretLabel,
    RequiredBadge,
    SecretDescription,
    InfoMessage,
    HiddenFileInput,
} from './styled';
import { RenderIf } from '@/components/RenderIf';

type ImportStep = 'upload' | 'mapping';

const ImportToolModal = ({ isOpen, onRequestClose, agentId, onSuccess }: ImportToolModalProps) => {
    const [step, setStep] = useState<ImportStep>('upload');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [parsedTool, setParsedTool] = useState<ParsedToolFile | null>(null);
    const [secretMapping, setSecretMapping] = useState<Record<string, string>>({});
    const [isDragActive, setIsDragActive] = useState(false);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { data: secrets } = useSecrets({ agentId });

    const { importTool, isImporting } = useImportTool({
        onSuccess: tool => {
            toast.success(`Tool '${tool.name}' imported successfully`);
            handleClose();
            onSuccess?.(tool);
        },
        onError: error => {
            setError(error.message);
        },
    });

    const validateAndParseFile = async (file: File): Promise<ParsedToolFile | null> => {
        try {
            const fileContent = await file.text();
            const toolExport = JSON.parse(fileContent) as SystemToolExport;

            // Validate export version
            if (toolExport.exportVersion !== '1.0.0') {
                setError(`Unsupported export version: ${toolExport.exportVersion}`);
                return null;
            }

            // Validate type
            if (toolExport.type !== 'system') {
                setError('Only system tools can be imported');
                return null;
            }

            // Validate structure
            if (!toolExport.systemRef || !toolExport.metadata || !toolExport.nodeParameters) {
                setError('Invalid tool export file format');
                return null;
            }

            return {
                toolExport,
                filename: file.name,
            };
        } catch (error) {
            if (error instanceof SyntaxError) {
                setError('Invalid JSON file. Please check the file format.');
            } else {
                setError('Failed to read the file. Please try again.');
            }
            return null;
        }
    };

    const handleFileSelect = async (file: File) => {
        setError('');
        setSelectedFile(file);

        const parsed = await validateAndParseFile(file);
        if (parsed) {
            setParsedTool(parsed);
        } else {
            setSelectedFile(null);
        }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);

        const file = event.dataTransfer.files[0];
        if (file && file.type === 'application/json') {
            handleFileSelect(file);
        } else {
            setError('Please drop a valid JSON file');
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = () => {
        setIsDragActive(false);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setParsedTool(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const renderProgressBar = () => (
        <RenderIf isTrue={isImporting}>
            <ProgressContainer>
                <LoadingText>Importing tool...</LoadingText>
                <Progress value={undefined} className="h-2" />
            </ProgressContainer>
        </RenderIf>
    );

    const handleContinue = () => {
        if (!parsedTool) return;

        // Check if tool has secret dependencies
        const hasSecrets = parsedTool.toolExport.dependencies.secrets.length > 0;

        if (hasSecrets) {
            setStep('mapping');
        } else {
            // No secrets needed, import directly
            handleImport();
        }
    };

    const handleSecretMappingChange = (paramPath: string, secretKey: string) => {
        setSecretMapping(prev => ({
            ...prev,
            [paramPath]: secretKey,
        }));
    };

    const canImport = (): boolean => {
        if (!parsedTool) return false;

        const requiredSecrets = parsedTool.toolExport.dependencies.secrets.filter(s => s.required);

        // Check all required secrets are mapped
        return requiredSecrets.every(s => secretMapping[s.paramPath]);
    };

    const handleImport = async () => {
        if (!parsedTool) return;

        setError('');

        await importTool({
            agentId,
            toolExport: parsedTool.toolExport,
            secretMapping,
        });
    };

    const handleBack = () => {
        setStep('upload');
        setError('');
    };

    const handleClose = () => {
        setStep('upload');
        setSelectedFile(null);
        setParsedTool(null);
        setSecretMapping({});
        setError('');
        setIsDragActive(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onRequestClose();
    };

    const renderUploadStep = () => (
        <>
            <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileInputChange}
                aria-label="Upload tool JSON file"
            />

            <RenderIf isTrue={!selectedFile}>
                <DropZone
                    isDragActive={isDragActive}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleClick}
                >
                    <UploadIcon>
                        <Upload size={48} />
                    </UploadIcon>
                    <DropZoneText>
                        {isDragActive ? 'Drop the file here' : 'Drag and drop a JSON file here'}
                    </DropZoneText>
                    <DropZoneSubtext>or click to browse files</DropZoneSubtext>
                </DropZone>
            </RenderIf>

            <RenderIf isTrue={!!selectedFile}>
                <SelectedFile>
                    <File size={24} color="#3b82f6" />
                    <FileInfo>
                        <FileName>{selectedFile?.name}</FileName>
                        <FileSize>
                            {selectedFile ? `${(selectedFile.size / 1024).toFixed(2)} KB` : ''}
                        </FileSize>
                    </FileInfo>
                    <RemoveButton onClick={handleRemoveFile} title="Remove file">
                        <X size={20} />
                    </RemoveButton>
                </SelectedFile>
            </RenderIf>

            {renderProgressBar()}

            <RenderIf isTrue={!!error}>
                <ErrorMessage>{error}</ErrorMessage>
            </RenderIf>

            <ButtonContainer>
                <Button variant="outline" onClick={handleClose} disabled={isImporting}>
                    Cancel
                </Button>
                <Button onClick={handleContinue} disabled={!parsedTool || isImporting}>
                    Continue
                </Button>
            </ButtonContainer>
        </>
    );

    const renderMappingStep = () => {
        if (!parsedTool) return null;

        const dependencies = parsedTool.toolExport.dependencies.secrets;

        return (
            <>
                <RenderIf isTrue={dependencies.length > 0}>
                    <InfoMessage>
                        This tool requires {dependencies.length} secret
                        {dependencies.length === 1 ? '' : 's'} to be configured. Please map each
                        secret to your existing secrets.
                    </InfoMessage>
                </RenderIf>

                <SecretMappingContainer>
                    {dependencies.map(secret => (
                        <SecretMappingItem key={secret.paramPath}>
                            <SecretLabel>
                                {secret.paramPath}
                                <RenderIf isTrue={secret.required}>
                                    <RequiredBadge>*</RequiredBadge>
                                </RenderIf>
                            </SecretLabel>
                            <SecretDescription>{secret.description}</SecretDescription>
                            <Select
                                value={secretMapping[secret.paramPath] || ''}
                                onValueChange={value =>
                                    handleSecretMappingChange(secret.paramPath, value)
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a secret" />
                                </SelectTrigger>
                                <SelectContent>
                                    {secrets.map(s => (
                                        <SelectItem key={s.key} value={s.key}>
                                            {s.key}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </SecretMappingItem>
                    ))}
                </SecretMappingContainer>

                {renderProgressBar()}

                <RenderIf isTrue={!!error}>
                    <ErrorMessage>{error}</ErrorMessage>
                </RenderIf>

                <ButtonContainer>
                    <Button variant="outline" onClick={handleBack} disabled={isImporting}>
                        Back
                    </Button>
                    <Button onClick={handleImport} disabled={!canImport() || isImporting}>
                        Import
                    </Button>
                </ButtonContainer>
            </>
        );
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleClose}
            header={
                <ModalHeader>
                    <ModalTitle>Import Tool</ModalTitle>
                    <ModalDescription>
                        <RenderIf isTrue={step === 'upload'}>
                            Upload a tool JSON file to import it into your workspace
                        </RenderIf>
                        <RenderIf isTrue={step === 'mapping'}>
                            Configure required secrets for this tool
                        </RenderIf>
                    </ModalDescription>
                </ModalHeader>
            }
        >
            <RenderIf isTrue={step === 'upload'}>{renderUploadStep()}</RenderIf>
            <RenderIf isTrue={step === 'mapping'}>{renderMappingStep()}</RenderIf>
        </Modal>
    );
};

export default ImportToolModal;
